import React, { useEffect, useState } from "react";
import { Button, Modal } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { ButtonLoading, FormCheck, FormInput } from "go-form";
import useFlash from "go-alert/AlertMessage";
import handleError from "go-app/services/errors";
import { formatDateTimeToString } from "go-core/components/Formatters/FormatDate";
import { LoadingContainer } from "go-core/components/Loading";
import { FormDatePicker } from "go-form/components/FormDatePicker";
import { selectOrganization } from "go-security/services/organizations/selectors";
import { CustomFieldTemplateApi } from "go-segment/services/types";
import { apiOrganization } from "../../../../../../../../../../services/Api/Organization/apiOrganization";
import { InvoiceApi, InvoiceDetailsApi, TaxApi } from "../../../../../../../../../../services/Api/Organization/types";
import { exportInvoicePdf } from "../../utils/InvoicePdf";
import { ExportInvoicePdfConfig } from "../../utils/types";

interface Props {
	show: boolean;
	onHide: () => void;
	customFields: CustomFieldTemplateApi[];
	invoice: InvoiceApi;
	taxes: TaxApi[];
}

const InvoicePdfGeneratorModal = ({ show, onHide, customFields, invoice, taxes }: Props) => {
	const { t } = useTranslation();
	const organization = useSelector(selectOrganization);
	const form = useForm<ExportInvoicePdfConfig>({
		criteriaMode: "all",
		defaultValues: {
			company: organization.more?.company_name,
			organization: organization.name,
			taxIdNo: organization.tax_id_no,
			printCustomFields: false,
			duplicate: false,
			group: false,
			customFields: [],
		},
	});
	const { register, watch, control, setValue } = form;
	const [invoiceDetails, setInvoiceDetails] = useState<InvoiceDetailsApi | undefined>();
	const { addFlash } = useFlash();
	const [loading, setLoading] = useState(false);
	const [printLoading, setPrintLoading] = useState(false);

	useEffect(() => {
		fetchInvoiceDetails();
	}, []);

	const fetchInvoiceDetails = async () => {
		try {
			const res = await apiOrganization.getInvoiceDetails();
			setInvoiceDetails(res);
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	const isCustomFieldSelected = (customField: CustomFieldTemplateApi) => {
		return (watch("customFields") || []).findIndex((cf) => cf.id === customField.id) > -1;
	};

	const handleCheckCustomField = (customField: CustomFieldTemplateApi) => {
		const currentCustomFields = [...(watch("customFields") || [])];

		const existingCustomFieldIndex = currentCustomFields.findIndex((cf) => cf.id === customField.id);
		if (existingCustomFieldIndex > -1) {
			currentCustomFields.splice(existingCustomFieldIndex, 1);
		} else {
			currentCustomFields.push(customField);
		}

		setValue("customFields", currentCustomFields);
	};

	const handleGenerate = async () => {
		setLoading(true);
		await exportInvoicePdf(watch(), t, invoice.items, invoice, invoice.summary, taxes, invoiceDetails!);
		setLoading(false);
		onHide();
	};

	const handlePrint = async () => {
		setPrintLoading(true);
		await exportInvoicePdf(watch(), t, invoice.items, invoice, invoice.summary, taxes, invoiceDetails!, true);
		setPrintLoading(false);
		onHide();
	};

	const handleChangeDuplicateCheckbox = (value?: boolean) => {
		setValue("duplicateDate", value ? formatDateTimeToString(new Date()) : undefined);
	};

	return (
		<Modal show={show} onHide={onHide}>
			<Modal.Header closeButton>
				<Modal.Title>{t("modules.invoice.field.invoice_print_options.title")}</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				{invoiceDetails ? (
					<div className="d-flex flex-column">
						<FormCheck
							label={t("modules.invoice.field.group.title")}
							name="group"
							register={register}
							errors={[]}
							className="ms-2"
						/>
						{watch("group") && (
							<FormInput
								name="groupName"
								errors={[]}
								register={register}
								label={t("lib:common.word.name")}
								defaultValue={t("modules.invoice.field.catering_service.title")}
							/>
						)}
						<FormCheck
							className="ms-2"
							label={t("modules.invoice.field.duplicate.title")}
							name="duplicate"
							register={register}
							errors={[]}
							onClick={(event) => handleChangeDuplicateCheckbox(event.currentTarget.checked)}
						/>
						{watch("duplicate") && (
							<FormDatePicker
								errors={[]}
								control={control}
								name="duplicateDate"
								label={t("modules.invoice.field.duplicate_date.title")}
								defaultValue={formatDateTimeToString(new Date())}
								allowClear={false}
							/>
						)}
						<FormCheck
							className="ms-2"
							label={t("modules.document.field.show_custom_fields.title")}
							name="printCustomFields"
							register={register}
							errors={[]}
						/>
						{watch("printCustomFields") && (
							<div className="mt-1">
								<h6>{t("modules.invoice.field.choose_custom_fields.title")}</h6>
								<div className="d-flex flex-wrap gap-2">
									{customFields.map((customField) => (
										<Button
											variant={`${
												isCustomFieldSelected(customField) ? "primary" : "outline-secondary"
											}`}
											key={customField.id}
											onClick={() => handleCheckCustomField(customField)}
										>
											{customField.name}
										</Button>
									))}
								</div>
							</div>
						)}
					</div>
				) : (
					<LoadingContainer />
				)}
			</Modal.Body>
			<Modal.Footer>
				<ButtonLoading loading={loading} onClick={handleGenerate}>
					{t("lib:common.action.generate")}
				</ButtonLoading>
				<ButtonLoading loading={printLoading} onClick={handlePrint}>
					{t("lib:common.action.print")}
				</ButtonLoading>
				<Button variant="light" onClick={onHide}>
					{t("lib:common.action.cancel")}
				</Button>
			</Modal.Footer>
		</Modal>
	);
};

export default InvoicePdfGeneratorModal;
